import { useState, useCallback, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { useCalendar } from '../../context/CalendarContext';
import { pixelsToTime, timeToPixels, snapToGrid } from '../../utils/calendar/timeCalculations';
import { checkMoveConflicts, checkResizeConflicts } from '../../utils/calendar/conflictDetection';
import { CALENDAR_CONSTANTS } from '../../utils/calendar/constants';

/**
 * Hook for handling drag and drop functionality in the calendar
 */
export const useTimeSlotDrag = ({
                                    onEventMove,
                                    onEventResize,
                                    onTimeRangeSelect,
                                    existingEvents = [],
                                    dayStart,
                                    hourHeight,
                                }) => {
    const {
        isDragging,
        isResizing,
        draggedEvent,
        startDragging,
        stopDragging,
        startResizing,
        stopResizing,
        snapToGrid: shouldSnapToGrid,
        snapMinutes,
    } = useCalendar();

    const [dragState, setDragState] = useState({
        startY: 0,
        startTime: null,
        currentTime: null,
        isValid: true,
        conflicts: [],
    });

    const [selectionState, setSelectionState] = useState({
        isSelecting: false,
        startY: 0,
        endY: 0,
        startTime: null,
        endTime: null,
    });

    const dragRef = useRef(dragState);
    const selectionRef = useRef(selectionState);

    // Update refs when state changes
    useEffect(() => {
        dragRef.current = dragState;
    }, [dragState]);

    useEffect(() => {
        selectionRef.current = selectionState;
    }, [selectionState]);

    // Start dragging an event
    const handleDragStart = useCallback((e, event) => {
        e.preventDefault();
        e.stopPropagation();

        const startY = e.clientY || (e.touches && e.touches[0].clientY);

        startDragging(event);
        setDragState({
            startY,
            startTime: dayjs(event.startTime),
            currentTime: dayjs(event.startTime),
            isValid: true,
            conflicts: [],
        });
    }, [startDragging]);

    // Start resizing an event
    const handleResizeStart = useCallback((e, event, edge) => {
        e.preventDefault();
        e.stopPropagation();

        const startY = e.clientY || (e.touches && e.touches[0].clientY);

        startResizing(event);
        setDragState({
            startY,
            startTime: dayjs(event.startTime),
            endTime: dayjs(event.endTime),
            currentTime: edge === 'top' ? dayjs(event.startTime) : dayjs(event.endTime),
            resizeEdge: edge,
            isValid: true,
            conflicts: [],
        });
    }, [startResizing]);

    // Start time range selection
    const handleSelectionStart = useCallback((e, clickTime) => {
        if (isDragging || isResizing) return;

        const startY = e.clientY || (e.touches && e.touches[0].clientY);
        const snappedTime = shouldSnapToGrid ? snapToGrid(clickTime, snapMinutes) : clickTime;

        setSelectionState({
            isSelecting: true,
            startY,
            endY: startY,
            startTime: snappedTime,
            endTime: snappedTime.add(snapMinutes, 'minute'),
        });
    }, [isDragging, isResizing, shouldSnapToGrid, snapMinutes]);

    // Handle mouse/touch move for dragging
    const handleDragMove = useCallback((e) => {
        if (!isDragging && !isResizing) return;

        const currentY = e.clientY || (e.touches && e.touches[0].clientY);
        const deltaY = currentY - dragRef.current.startY;

        // Calculate new time based on pixel movement
        const deltaMinutes = Math.round((deltaY / hourHeight) * 60);
        let newTime;

        if (isResizing) {
            const { resizeEdge, startTime, endTime } = dragRef.current;

            if (resizeEdge === 'top') {
                newTime = startTime.add(deltaMinutes, 'minute');
                if (shouldSnapToGrid) newTime = snapToGrid(newTime, snapMinutes);

                // Ensure minimum duration
                const minEndTime = newTime.add(CALENDAR_CONSTANTS.MIN_DURATION_MINUTES, 'minute');
                if (minEndTime.isAfter(endTime)) return;

                // Check conflicts
                const conflictCheck = checkResizeConflicts(
                    draggedEvent,
                    newTime,
                    endTime,
                    existingEvents
                );

                setDragState(prev => ({
                    ...prev,
                    currentTime: newTime,
                    isValid: !conflictCheck.hasConflict,
                    conflicts: conflictCheck.conflicts,
                }));
            } else {
                newTime = endTime.add(deltaMinutes, 'minute');
                if (shouldSnapToGrid) newTime = snapToGrid(newTime, snapMinutes);

                // Ensure minimum duration
                const minStartTime = newTime.subtract(CALENDAR_CONSTANTS.MIN_DURATION_MINUTES, 'minute');
                if (minStartTime.isBefore(startTime)) return;

                // Check conflicts
                const conflictCheck = checkResizeConflicts(
                    draggedEvent,
                    startTime,
                    newTime,
                    existingEvents
                );

                setDragState(prev => ({
                    ...prev,
                    currentTime: newTime,
                    isValid: !conflictCheck.hasConflict,
                    conflicts: conflictCheck.conflicts,
                }));
            }
        } else {
            // Moving entire event
            newTime = dragRef.current.startTime.add(deltaMinutes, 'minute');
            if (shouldSnapToGrid) newTime = snapToGrid(newTime, snapMinutes);

            // Check conflicts
            const conflictCheck = checkMoveConflicts(
                draggedEvent,
                newTime,
                existingEvents
            );

            setDragState(prev => ({
                ...prev,
                currentTime: newTime,
                isValid: !conflictCheck.hasConflict,
                conflicts: conflictCheck.conflicts,
            }));
        }
    }, [isDragging, isResizing, hourHeight, shouldSnapToGrid, snapMinutes, draggedEvent, existingEvents]);

    // Handle mouse/touch move for selection
    const handleSelectionMove = useCallback((e) => {
        if (!selectionRef.current.isSelecting) return;

        const currentY = e.clientY || (e.touches && e.touches[0].clientY);
        const { startY, startTime } = selectionRef.current;

        // Calculate end time based on mouse position
        const deltaY = currentY - startY;
        const deltaMinutes = Math.round((deltaY / hourHeight) * 60);
        let endTime = startTime.add(deltaMinutes, 'minute');

        if (shouldSnapToGrid) {
            endTime = snapToGrid(endTime, snapMinutes);
        }

        // Ensure minimum selection
        if (endTime.isBefore(startTime)) {
            endTime = startTime.add(snapMinutes, 'minute');
        }

        setSelectionState(prev => ({
            ...prev,
            endY: currentY,
            endTime,
        }));
    }, [hourHeight, shouldSnapToGrid, snapMinutes]);

    // Handle drag end
    const handleDragEnd = useCallback(() => {
        if (!isDragging && !isResizing) return;

        const { isValid, currentTime, resizeEdge } = dragRef.current;

        if (isValid) {
            if (isResizing) {
                // Calculate new times for resize
                const newStartTime = resizeEdge === 'top' ? currentTime : dayjs(draggedEvent.startTime);
                const newEndTime = resizeEdge === 'bottom' ? currentTime : dayjs(draggedEvent.endTime);

                onEventResize?.(draggedEvent, newStartTime, newEndTime);
            } else {
                // Calculate new times for move
                const duration = dayjs(draggedEvent.endTime).diff(dayjs(draggedEvent.startTime), 'minute');
                const newEndTime = currentTime.add(duration, 'minute');

                onEventMove?.(draggedEvent, currentTime, newEndTime);
            }
        }

        // Reset state
        stopDragging();
        stopResizing();
        setDragState({
            startY: 0,
            startTime: null,
            currentTime: null,
            isValid: true,
            conflicts: [],
        });
    }, [isDragging, isResizing, draggedEvent, onEventMove, onEventResize, stopDragging, stopResizing]);

    // Handle selection end
    const handleSelectionEnd = useCallback(() => {
        if (!selectionRef.current.isSelecting) return;

        const { startTime, endTime } = selectionRef.current;

        if (startTime && endTime && !startTime.isSame(endTime)) {
            onTimeRangeSelect?.(startTime, endTime);
        }

        setSelectionState({
            isSelecting: false,
            startY: 0,
            endY: 0,
            startTime: null,
            endTime: null,
        });
    }, [onTimeRangeSelect]);

    // Calculate ghost position for dragging
    const getGhostPosition = useCallback(() => {
        if (!isDragging && !isResizing) return null;

        const { currentTime, isValid } = dragState;
        if (!currentTime || !dayStart) return null;

        const top = timeToPixels(currentTime, dayStart, hourHeight);

        return {
            top,
            isValid,
            time: currentTime,
        };
    }, [isDragging, isResizing, dragState, dayStart, hourHeight]);

    // Calculate selection box dimensions
    const getSelectionBox = useCallback(() => {
        if (!selectionState.isSelecting) return null;

        const { startTime, endTime } = selectionState;
        if (!startTime || !endTime || !dayStart) return null;

        const top = timeToPixels(startTime, dayStart, hourHeight);
        const bottom = timeToPixels(endTime, dayStart, hourHeight);

        return {
            top: Math.min(top, bottom),
            height: Math.abs(bottom - top),
            startTime: startTime.isBefore(endTime) ? startTime : endTime,
            endTime: startTime.isBefore(endTime) ? endTime : startTime,
        };
    }, [selectionState, dayStart, hourHeight]);

    return {
        // State
        isDragging,
        isResizing,
        draggedEvent,
        isSelecting: selectionState.isSelecting,
        dragState,
        selectionState,

        // Handlers
        handleDragStart,
        handleResizeStart,
        handleSelectionStart,
        handleDragMove,
        handleSelectionMove,
        handleDragEnd,
        handleSelectionEnd,

        // Computed values
        ghostPosition: getGhostPosition(),
        selectionBox: getSelectionBox(),
    };
};